// src/pages/ResultsMaster.tsx
// REFACTORED: Verwendet nur result.rawAnswers, result.normalizedData, result.computedMetrics, result.dataGaps

import React, { useMemo, useState, useEffect, useRef } from "react";
import { Layout } from "@/components/Layout";
import { useNavigate } from "react-router-dom";
import { useAuditStore } from "@/hooks/useAuditStore";
import { useAdminStore } from "@/hooks/useAdminStore";
import { ROUTE_PATHS } from "@/lib/index";
import { downloadResultsReport } from "@/lib/resultsReportGenerator";
import { DIMENSION_LABELS } from "@/lib/master-diagnostic-system";
import { getLeverMetrics, inferDimensionFromTitle } from "@/lib/lever-metrics-mapper";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ChevronRight,
  Download,
  Gauge,
  LineChart,
  Target,
  TrendingUp,
  Users,
  Zap,
  Activity,
  Workflow,
} from "lucide-react";

// STRIPE-INSPIRED DESIGN - Clean, minimal, generous whitespace
const card = "stripe-card";
const softCard = "rounded-xl border border-border bg-card p-6";
const accentCard = "rounded-2xl border-2 border-accent/20 bg-accent/5 p-6";
const sectionTitle = "text-3xl font-bold text-foreground mb-3";
const sectionSub = "text-lg text-muted-foreground leading-relaxed";
const badge = "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider";

// Helper functions
const num = (value: number | null | undefined, decimals = 0): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "0";
  return value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const currency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "0 €";
  return `${num(value, 0)} €`;
};

const pct = (value: number | null | undefined, decimals = 1): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "0%";
  return `${value.toFixed(decimals)}%`;
};

const fmtNumber = (value: number | null | undefined, digits = 1): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "–";
  return Number(value).toLocaleString("de-DE", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const fmtInt = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "–";
  return Math.round(Number(value)).toLocaleString("de-DE");
};

type SeverityLevel = "good" | "medium" | "high" | "neutral";

function SeverityBadge({ severity }: { severity: SeverityLevel }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold",
        severity === "good" && "bg-emerald-100 text-emerald-700",
        severity === "medium" && "bg-amber-100 text-amber-700",
        severity === "high" && "bg-red-100 text-red-700",
        severity === "neutral" && "bg-slate-100 text-slate-600"
      )}
    >
      {severity === "good" ? "stark" : severity === "medium" ? "mittel" : severity === "high" ? "kritisch" : "offen"}
    </span>
  );
}

// -------------------------------------------------------
// JOURNEY STEPS — fix vorgegeben, keine eigene Logik
// -------------------------------------------------------
const JOURNEY_STEPS = [
  { id: "journey-reality",       label: "Reality",       desc: "Wo stehen Sie heute?",                          targetId: "section-business-reality" },
  { id: "journey-ursachen",      label: "Ursachen",      desc: "Warum bleibt die Performance hinter dem Ziel?", targetId: "section-strukturelle-ursachen" },
  { id: "journey-performance",   label: "Performance",   desc: "Wie groß ist der Abstand zum Ziel?",            targetId: "section-team-pace" },
  { id: "journey-hebel",         label: "Top-Hebel",     desc: "Welche Maßnahmen haben den größten Effekt?",    targetId: "section-top-hebel" },
  { id: "journey-loesung",       label: "Plan",          desc: "Wie setzen Sie das konkret um?",                targetId: "section-handlungsempfehlung" },
] as const;

// Smooth scroll to section by ID
function scrollToSection(targetId: string) {
  const el = document.getElementById(targetId);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

export default function ResultsMaster() {
  const navigate = useNavigate();
  const { result, companyProfile, rawAnswers } = useAuditStore();

  // Journey Scroll-Tracking
  const [activeStep, setActiveStep] = useState<string>(JOURNEY_STEPS[0].id);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current?.disconnect();
    const visibleRatios = new Map<string, number>();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibleRatios.set(entry.target.id, entry.intersectionRatio);
        });
        // Find the section with the highest visible ratio
        let bestId = "";
        let bestRatio = 0;
        visibleRatios.forEach((ratio, id) => {
          if (ratio > bestRatio) { bestRatio = ratio; bestId = id; }
        });
        if (bestId) {
          const step = JOURNEY_STEPS.find((s) => s.targetId === bestId);
          if (step) setActiveStep(step.id);
        }
      },
      { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0], rootMargin: "-10% 0px -30% 0px" }
    );

    JOURNEY_STEPS.forEach((step) => {
      const el = document.getElementById(step.targetId);
      if (el) observerRef.current!.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [result]);

  if (!result || !result.normalizedData) {
    return (
      <Layout hideFooterCta>
        <div className="min-h-screen bg-background flex items-center justify-center p-8">
          <div className="max-w-md text-center">
            <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted">
              <AlertTriangle className="h-8 w-8 text-muted-foreground" />
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-4">Keine Daten verfügbar</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Bitte führen Sie zuerst das Audit durch, um Ihre Ergebnisse zu sehen.
            </p>
            <button
              onClick={() => navigate(ROUTE_PATHS.AUDIT)}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Zum Audit
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Extract data from result (SINGLE SOURCE OF TRUTH)
  const {
    normalizedData,
    computedMetrics,
    dataGaps,
    topLevers,
    acquisitionMethods,
    diagnosticScoring,
    teamPerformance,
    structuralCauses,
  } = result;

  // Acquisition Mix
  type ChannelShare = { key: string; label: string; value: number };
  type ChannelInsight = ChannelShare & { scalability: string; cycleImpact: string; conversionProfile: string; riskNote?: string };
  type SourceMix = { shares: ChannelShare[]; dominant: ChannelShare | null; isDiversified: boolean; hasData: boolean; insights: ChannelInsight[]; riskLevel: 'high' | 'medium' | 'low' | 'unknown'; strategicNote: string };

  const sourceMix = useMemo((): SourceMix => {
    const methods = acquisitionMethods || {};
    const entries = Object.entries(methods);
    if (entries.length === 0) {
      return { shares: [], dominant: null, isDiversified: false, hasData: false, insights: [], riskLevel: 'unknown', strategicNote: '' };
    }

    const shares = entries
      .map(([key, value]) => ({
        key,
        label: getLabelForMethod(key),
        value: Number(value) || 0,
      }))
      .filter((s) => s.value > 0)
      .sort((a, b) => b.value - a.value);

    const total = shares.reduce((sum, s) => sum + s.value, 0);
    const hasData = total > 0;
    const dominant = hasData && shares.length > 0 && shares[0].value >= 50 ? shares[0] : null;
    const isDiversified = hasData && shares.length >= 3 && shares[0].value < 50;

    // Kanal-Charakteristik: Jeder Kanal hat spezifische Eigenschaften
    const channelTraits: Record<string, { scalability: string; cycleImpact: string; conversionProfile: string; riskNote?: string }> = {
      phone:           { scalability: 'Mittel', cycleImpact: 'Verkürzt Sales Cycle (direkter Dialog)', conversionProfile: 'Hoch bei qualifizierten Leads', riskNote: 'Abhängig von Rep-Kapazität und Gesprächsqualität' },
      email:           { scalability: 'Hoch', cycleImpact: 'Verlängert tendenziell (async)', conversionProfile: 'Niedrig ohne starke Sequenz', riskNote: 'Sinkende Zustellraten, hoher Wettbewerb im Postfach' },
      linkedin:        { scalability: 'Mittel', cycleImpact: 'Neutral bis verkürzend (Warm-up)', conversionProfile: 'Mittel — stark bei persönlicher Note', riskNote: 'Algorithmus-Abhängigkeit, Plattformrisiko' },
      xing:            { scalability: 'Niedrig', cycleImpact: 'Neutral', conversionProfile: 'Gering — schrumpfende Reichweite', riskNote: 'Relevanz stark rückläufig im DACH-Raum' },
      referrals:       { scalability: 'Niedrig', cycleImpact: 'Stark verkürzend (Vertrauen vorhanden)', conversionProfile: 'Sehr hoch — höchste Abschlussrate aller Kanäle', riskNote: 'Nicht skalierbar ohne aktives Empfehlungssystem' },
      events:          { scalability: 'Niedrig', cycleImpact: 'Verkürzend (persönlicher Kontakt)', conversionProfile: 'Mittel bis hoch', riskNote: 'Hoher Aufwand, saisonale Abhängigkeit' },
      contentMarketing:{ scalability: 'Sehr hoch', cycleImpact: 'Verlängernd (längere Nurture-Strecke)', conversionProfile: 'Niedrig kurzfristig, hoch langfristig', riskNote: 'Zeitverzögert — Ergebnisse erst nach 6–18 Monaten' },
      paidAds:         { scalability: 'Sehr hoch', cycleImpact: 'Neutral', conversionProfile: 'Niedrig ohne starke Landing Page', riskNote: 'Kostenintensiv, sofort wirkungslos bei Budgetstopp' },
      coldVisits:      { scalability: 'Sehr niedrig', cycleImpact: 'Stark verkürzend', conversionProfile: 'Mittel — hoher Aufwand pro Lead', riskNote: 'Extrem ressourcenintensiv, kaum skalierbar' },
      partner:         { scalability: 'Hoch', cycleImpact: 'Verkürzend (Partnervertrauen)', conversionProfile: 'Hoch bei passendem Partner-ICP', riskNote: 'Abhängigkeit vom Partner-Commitment' },
      other:           { scalability: 'Unbekannt', cycleImpact: 'Unbekannt', conversionProfile: 'Unbekannt', riskNote: undefined },
    };

    // Top 3 Kanäle mit Insights anreichern
    const insights = shares.slice(0, 4).map(s => ({
      ...s,
      ...(channelTraits[s.key] || { scalability: 'Unbekannt', cycleImpact: 'Unbekannt', conversionProfile: 'Unbekannt' }),
    }));

    // Risikobewertung des Gesamtmix
    const outboundShare = (methods.phone || 0) + (methods.email || 0) + (methods.linkedin || 0) + (methods.coldVisits || 0);
    const inboundShare = (methods.contentMarketing || 0) + (methods.paidAds || 0);
    const referralShare = (methods.referrals || 0) + (methods.partner || 0);
    const topShare = shares[0]?.value || 0;

    let riskLevel: 'high' | 'medium' | 'low' = 'low';
    let strategicNote = '';

    if (topShare >= 60) {
      riskLevel = 'high';
      strategicNote = `Ihr Mix hat ein strukturelles Klumpenrisiko: ${shares[0].value}% Ihrer Neukundengewinnung hängen von einem einzigen Kanal ab (${shares[0].label}). Fällt dieser Kanal weg oder skaliert er nicht mehr, bricht Ihre Pipeline ein. B2B-Teams mit stabiler Pipeline nutzen mindestens 3 Kanäle mit je 15–40% Anteil.`;
    } else if (outboundShare >= 70) {
      riskLevel = 'medium';
      strategicNote = `Ihr Mix ist stark outbound-lastig (${Math.round(outboundShare)}%). Das bedeutet: Wachstum ist direkt an Rep-Kapazität geknüpft — jeder neue Lead kostet aktive Arbeitszeit. Ohne parallelen Aufbau von Inbound oder Referral-Kanälen ist nachhaltiges Skalieren aufwändig.`;
    } else if (referralShare >= 40) {
      riskLevel = 'medium';
      strategicNote = `${Math.round(referralShare)}% Ihrer Neukundengewinnung laufen über Empfehlungen und Partner — die Kanal-Qualität ist hoch, aber die Vorhersagbarkeit gering. Empfehlungsgeschäft lässt sich kaum planen. Ein stärkerer Outbound- oder Inbound-Anteil würde den Forecast planrbarer machen.`;
    } else if (isDiversified && inboundShare >= 20) {
      riskLevel = 'low';
      strategicNote = `Ihr Akquise-Mix ist ausgewogen: Sie kombinieren skalierbare Inbound-Kanäle (${Math.round(inboundShare)}%) mit aktivem Outbound. Das ist die robusteste Basis für planbare Pipeline. Der Fokus sollte jetzt auf Conversion-Optimierung je Kanal liegen — nicht auf Kanalausbau.`;
    } else {
      riskLevel = 'medium';
      strategicNote = `Ihr Mix zeigt eine solide Verteilung, aber ohne klare Skalierungshebel. Die nächste Wachstumsstufe erfordert einen Kanal, der ohne linearen Rep-Einsatz mehr Leads generiert — typischerweise Content, Paid oder ein strukturiertes Partnerprogramm.`;
    }

    return { shares, dominant, isDiversified, hasData, insights, riskLevel, strategicNote };
  }, [acquisitionMethods]);

// STRIPE-INSPIRED DESIGN (override)
  const card = "stripe-card";
  const softCard = "rounded-xl border border-border bg-card p-6";
  const sectionTitle = "text-2xl font-bold text-foreground mb-2";
  const sectionSub = "text-muted-foreground text-sm leading-relaxed";
  const badge = "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold";

  // Get scenarios from result
  const scenarios = result.simulation?.scenarios || null;
  const completenessScore = result.completenessScore || 0;

  // Calculate team productivity
  const revenuePerRep = normalizedData.teamSize > 0
    ? normalizedData.monthlyRevenue / normalizedData.teamSize
    : 0;

  // ---- Audit-Rohdaten & KPI-Variablen ----
  const goalPath = computedMetrics?.goalPath;
  const rawData = rawAnswers as Record<string, unknown>;

  // ---- SCHWACHSTELLEN-FIXES: Qualitäts-Signale ----
  // Fix 1: 0-Abschlüsse-Signal — Kein echter Messwert vorhanden
  const hasZeroDeals = normalizedData.monthlyDeals === 0;
  // Fix 2: Datenvollständigkeit — Konfidenz-Level
  const dataGapCount = (dataGaps || []).length;
  const lowConfidence = completenessScore < 50 || dataGapCount >= 6;
  const mediumConfidence = !lowConfidence && (completenessScore < 70 || dataGapCount >= 3);
  // Fix 3: Sales Cycle zu lang für Jahresende
  const salesCycleTooLongForYearEnd = (() => {
    const scMonths = normalizedData.salesCycleMonths || 0;
    if (scMonths <= 0) return false;
    const now = new Date();
    const monthsRemaining = 12 - now.getMonth();
    return scMonths >= monthsRemaining;
  })();

  const _getRaw = (key: string): number | null => {
    const v = rawData?.[key];
    if (v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const qualifiedLeadRate = _getRaw("qualifiedLeadRatePercentExact");
  const proposalsWithNextStep = _getRaw("proposalsWithNextStepPercentExact");
  const stalePipelineShare = _getRaw("stalePipelineSharePercentExact");
  const decisionMakerInFirstMeeting = _getRaw("decisionMakerInFirstMeetingPercentExact");
  // ---- END VARS ----

  return (
    <Layout hideFooterCta>
    <div className="min-h-screen bg-background">
      {/* MOBILE JOURNEY BAR */}
      <div className="md:hidden sticky top-0 z-40 bg-background/98 backdrop-blur-sm border-b border-border/60">
        <div className="px-3 py-2 overflow-x-auto scrollbar-none">
          <div className="flex items-end gap-0 min-w-max">
            {JOURNEY_STEPS.map((step, idx) => {
              const isActive = activeStep === step.id;
              const activeIdx = JOURNEY_STEPS.findIndex(s => s.id === activeStep);
              const isPast = idx < activeIdx;
              return (
                <React.Fragment key={step.id}>
                  <div
                    className="flex flex-col items-center gap-1 cursor-pointer px-1"
                    onClick={() => scrollToSection(step.targetId)}
                    role="button"
                    aria-label={`Zu ${step.label} springen`}
                  >
                    {/* Balken */}
                    <div className={cn(
                      "rounded-full transition-all duration-400",
                      isActive
                        ? "h-2 w-10 bg-primary shadow-sm"
                        : isPast
                        ? "h-1 w-8 bg-primary/35"
                        : "h-1 w-6 bg-muted-foreground/20"
                    )} />
                    {/* Label */}
                    <span className={cn(
                      "whitespace-nowrap transition-all duration-300",
                      isActive
                        ? "text-[10px] font-bold text-primary"
                        : isPast
                        ? "text-[9px] font-medium text-primary/50"
                        : "text-[9px] font-medium text-muted-foreground/40"
                    )}>
                      {step.label}
                    </span>
                  </div>
                  {idx < JOURNEY_STEPS.length - 1 && (
                    <div className={cn(
                      "h-px w-2 mb-4 shrink-0 transition-colors duration-400",
                      isPast ? "bg-primary/20" : "bg-muted-foreground/10"
                    )} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* DESKTOP LAYOUT — Sticky Sidebar links + Content rechts */}
      <div className="relative flex">
        {/* JOURNEY SIDEBAR — Desktop only */}
        <aside className="hidden md:block" aria-label="Analyse-Übersicht">
          <div className="sticky top-10 w-48 shrink-0 pl-5 pr-3 pt-10">

            {/* Header */}
            <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-muted-foreground/40 mb-6 pl-8">
              Analyse
            </p>

            <nav>
              <ol className="relative flex flex-col">
                {JOURNEY_STEPS.map((step, idx) => {
                  const isActive = activeStep === step.id;
                  const activeIdx = JOURNEY_STEPS.findIndex(s => s.id === activeStep);
                  const isPast = idx < activeIdx;
                  const isFuture = !isActive && !isPast;

                  return (
                    <li
                      key={step.id}
                      className="relative flex items-stretch gap-3 cursor-pointer group/item"
                      onClick={() => scrollToSection(step.targetId)}
                      role="button"
                      aria-label={`Zu ${step.label} springen`}
                    >
                      {/* Track: dot + line */}
                      <div className="flex flex-col items-center w-5 shrink-0">

                        {/* DOT */}
                        <div className={cn(
                          "shrink-0 rounded-full transition-all duration-500 ease-out",
                          isActive
                            ? "h-3 w-3 mt-1 bg-primary ring-4 ring-primary/20 shadow-sm"
                            : isPast
                            ? "h-2 w-2 mt-1.5 bg-primary/50"
                            : "h-1.5 w-1.5 mt-1.5 bg-muted-foreground/20"
                        )} />

                        {/* LINE */}
                        {idx < JOURNEY_STEPS.length - 1 && (
                          <div className={cn(
                            "w-px flex-1 mt-1 mb-0 rounded-full transition-all duration-500",
                            isPast
                              ? "bg-primary/40"
                              : isActive
                              ? "bg-gradient-to-b from-primary/40 to-muted-foreground/10"
                              : "bg-muted-foreground/12"
                          )} />
                        )}
                      </div>

                      {/* Text area */}
                      <div className={cn(
                        "min-w-0 transition-all duration-300",
                        isActive ? "pb-5" : "pb-4",
                        idx === JOURNEY_STEPS.length - 1 && "pb-0"
                      )}>

                        {/* Label */}
                        <span className={cn(
                          "block leading-none transition-all duration-300 group-hover/item:text-primary",
                          isActive
                            ? "text-[13px] font-bold text-primary mt-0.5"
                            : isPast
                            ? "text-[11px] font-medium text-foreground/45 mt-1"
                            : "text-[11px] font-normal text-muted-foreground/30 mt-1"
                        )}>
                          {step.label}
                        </span>

                        {/* Description — nur beim aktiven Step sichtbar */}
                        <span className={cn(
                          "block text-[10px] leading-snug transition-all duration-400 overflow-hidden",
                          isActive
                            ? "max-h-8 mt-1 text-primary/60 opacity-100"
                            : "max-h-0 mt-0 opacity-0"
                        )}>
                          {step.desc}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <div className="flex-1 min-w-0">
      <div className="stripe-container stripe-section">
        {/* HERO SECTION - Landingpage-Style */}
        <section id="section-business-reality" className="mb-16">
          {/* ── HERO BLOCK — wie Landingpage ── */}
          <div className="relative overflow-hidden rounded-3xl shadow-2xl mb-10 bg-slate-950 text-white">
            {/* Hintergrund-Akzente — wie Landingpage */}
            <div className="pointer-events-none absolute inset-0 z-0">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(30,60,120,0.5),transparent)]" />
              <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
              <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/15 rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-accent/10 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 px-8 py-10 md:px-12 md:py-14">
              {/* Top bar: Badge + PDF-Button */}
              <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/8 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-300">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  Sales Audit Ergebnisse
                </div>
                <button
                  onClick={() => {
                    try { downloadResultsReport(result); }
                    catch (err) { alert('PDF-Export fehlgeschlagen: ' + String(err)); }
                  }}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 px-4 py-2 text-xs font-semibold text-white transition-colors"
                >
                  <Download className="h-3.5 w-3.5" />
                  PDF herunterladen
                </button>
              </div>

              {/* Headline */}
              <h1 className="text-4xl md:text-5xl font-black text-white leading-tight tracking-tight max-w-3xl mb-4">
                {normalizedData.companyName
                  ? <>{normalizedData.companyName}:<br className="hidden md:block" /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-emerald-400 to-accent">Ihre individuelle Vertriebsdiagnose</span></>
                  : <>Ihre <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-emerald-400 to-accent">individuelle Vertriebsdiagnose</span></>}
              </h1>

              {/* Subtext */}
              <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mb-8">
                Basierend auf Ihren Eingaben in 7 Vertriebsdimensionen — eine datengestützte Analyse, wo Ihr Vertrieb Umsatz liegen lässt und was jetzt entscheidend ist.
              </p>

              {/* 0-ABSCHLÜSSE-WARNUNG — Fix 1 */}
              {hasZeroDeals && (
                <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3">
                  <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs font-bold text-amber-200 block mb-0.5">Kein echter Abschluss-Messwert</span>
                    <span className="text-xs text-amber-200/80">Da aktuell keine Abschlüsse erfasst sind, basieren die Potenzialberechnungen ausschließlich auf Benchmarks und Zielvorgaben — nicht auf realen Deal-Daten. Die Ergebnisse zeigen das rechnerische Potenzial, nicht die aktuelle Performance.</span>
                  </div>
                </div>
              )}

              {/* KPI-Zeile */}
              {(computedMetrics?.annualRevenueGap > 0 || computedMetrics?.monthlyRevenueGap > 0) && (
                <div className="flex flex-wrap gap-4">
                  {computedMetrics?.annualRevenueGap > 0 && (
                    <div className="rounded-2xl border border-white/15 bg-white/8 px-6 py-4 backdrop-blur-sm">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Jährliches Umsatzpotenzial</div>
                      <div className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-accent to-emerald-400">{currency(computedMetrics.annualRevenueGap)}</div>
                      <div className="text-xs text-slate-500 mt-0.5">abgeleitet aus Ihrer aktuellen Conversion vs. Benchmark</div>
                    </div>
                  )}
                  {computedMetrics?.monthlyRevenueGap > 0 && (
                    <div className="rounded-2xl border border-white/15 bg-white/8 px-6 py-4 backdrop-blur-sm">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Monatlicher Umsatzhebel</div>
                      <div className="text-3xl font-black text-white">{currency(computedMetrics.monthlyRevenueGap)}</div>
                      <div className="text-xs text-slate-500 mt-0.5">basierend auf aktueller Zielabweichung und Abschlussquote</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── DATEN-KONFIDENZ-BANNER — Fix 2 */}
          {lowConfidence && (
            <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-amber-900 mb-1">
                  Eingeschränkte Analysetiefe — {completenessScore}% der Felder ausgefüllt
                </div>
                <p className="text-sm text-amber-800 leading-relaxed">
                  Bei dieser Datenbasis ({dataGapCount} unbekannte Felder) können nicht alle Berechnungen mit voller Präzision durchgeführt werden.
                  Die Ergebnisse basieren teilweise auf Branchen-Benchmarks statt auf Ihren realen Daten.
                </p>
              </div>
            </div>
          )}
          {mediumConfidence && (
            <div className="mb-5 flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-5 py-3">
              <span className="text-blue-500 text-base shrink-0">ℹ️</span>
              <p className="text-sm text-blue-800">
                <strong>Analyse-Konfidenz: Mittel</strong> — {completenessScore}% der Felder ausgefüllt. Einige Berechnungen nutzen Benchmark-Werte als Ergänzung.
              </p>
            </div>
          )}

          {/* DATENLÜCKEN-DIAGNOSE: Spezifische Empfehlungen pro fehlendem Feld */}
          {dataGapCount > 0 && (() => {
            // Definiere pro Datenlücke eine spezifische Empfehlung
            const gapRecommendations: Record<string, { priority: '🔴 KRITISCH' | '🟡 HOCH' | '🟢 OK'; label: string; action: string }> = {
              conversionLeadToDiscovery:    { priority: '🔴 KRITISCH', label: 'Leads → Discovery Conversion',     action: 'Tracking in CRM implementieren: Wie viele Leads führen zu Erstgesprächen?' },
              conversionDiscoveryToOffer:   { priority: '🔴 KRITISCH', label: 'Discovery → Angebot Conversion',   action: 'Deal-Stage-Reporting aktivieren: Anteil der Calls mit Angebotspotenzial erfassen.' },
              conversionOfferToClose:       { priority: '🔴 KRITISCH', label: 'Angebot → Abschluss Conversion',   action: 'Win/Loss-Analyse einführen: Ursachen für verlorene Angebote dokumentieren.' },
              monthlyContacts:              { priority: '🟡 HOCH',     label: 'Monatliche Kontaktzahl',           action: 'Activity-Tracking einrichten: Outbound-Kontakte pro Rep pro Monat messen.' },
              monthlyDeals:                 { priority: '🟡 HOCH',     label: 'Monatliche Abschlüsse',            action: 'CRM-Pflicht: Jeder Abschluss muss im Datum und Wert im CRM erfasst werden.' },
              averageDealSize:              { priority: '🟡 HOCH',     label: 'Durchschnittlicher Deal-Wert',     action: 'ARR/Einmalumsatz je Abschluss in CRM tracken, Segmentierung nach Kundengröße.' },
              salesCycleMonths:             { priority: '🟡 HOCH',     label: 'Sales Cycle Länge',               action: 'Datum von Erst-Kontakt bis Abschluss erfassen — Median über letzte 10 Deals.' },
              responseTime:                 { priority: '🟡 HOCH',     label: 'Lead-Reaktionszeit',               action: 'Lead-Eingang vs. erster Kontakt automatisch loggen (CRM-Timestamp).' },
              teamSize:                     { priority: '🟢 OK',       label: 'Teamgröße',                       action: 'Headcount Vertrieb + SDR dokumentieren, inkl. Teilzeit-Anteile.' },
            };

            const knownGaps = (dataGaps || []).filter(g => typeof g === 'string') as string[];
            const criticalGaps = knownGaps.filter(g => gapRecommendations[g]?.priority === '🔴 KRITISCH');
            const highGaps = knownGaps.filter(g => gapRecommendations[g]?.priority === '🟡 HOCH');
            const unknownGaps = knownGaps.filter(g => !gapRecommendations[g]);

            const totalConvUnknown = criticalGaps.length;

            return (
              <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-slate-800">🔍 Datenlücken-Diagnose</div>
                    <div className="text-xs text-slate-500 mt-0.5">{dataGapCount} fehlende Datenpunkte — priorisierte Handlungsempfehlungen</div>
                  </div>
                  <div className={`text-xs font-bold px-3 py-1 rounded-full ${
                    totalConvUnknown >= 3 ? 'bg-red-100 text-red-700' :
                    totalConvUnknown >= 1 ? 'bg-amber-100 text-amber-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {totalConvUnknown >= 3 ? 'Conversion-Tracking implementieren' :
                     totalConvUnknown >= 1 ? 'Fehlende Daten erfassen' :
                     'Keine kritischen Lücken'}
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {criticalGaps.map(g => (
                    <div key={g} className="flex items-start gap-3 px-5 py-3">
                      <span className="text-xs mt-0.5 shrink-0">🔴</span>
                      <div>
                        <div className="text-xs font-semibold text-slate-800">{gapRecommendations[g].label}</div>
                        <div className="text-xs text-slate-600 mt-0.5">{gapRecommendations[g].action}</div>
                      </div>
                    </div>
                  ))}
                  {highGaps.map(g => (
                    <div key={g} className="flex items-start gap-3 px-5 py-3">
                      <span className="text-xs mt-0.5 shrink-0">🟡</span>
                      <div>
                        <div className="text-xs font-semibold text-slate-800">{gapRecommendations[g].label}</div>
                        <div className="text-xs text-slate-600 mt-0.5">{gapRecommendations[g].action}</div>
                      </div>
                    </div>
                  ))}
                  {unknownGaps.slice(0, 3).map(g => (
                    <div key={g} className="flex items-start gap-3 px-5 py-3">
                      <span className="text-xs mt-0.5 shrink-0">⚪</span>
                      <div>
                        <div className="text-xs font-semibold text-slate-700">{g}</div>
                        <div className="text-xs text-slate-500 mt-0.5">Dieses Feld nach Möglichkeit im nächsten Audit befüllen.</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* SALES-CYCLE-WARNUNG — Fix 3 */}
          {salesCycleTooLongForYearEnd && teamPerformance && (teamPerformance.totalRevenueTargetYear || 0) > 0 && (
            <div className="mb-5 flex items-start gap-3 rounded-xl border border-orange-200 bg-orange-50 px-5 py-4">
              <AlertTriangle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-orange-900 mb-1">
                  Sales Cycle ({normalizedData.salesCycleMonths} Monate) überschreitet verbleibende Jahreszeit
                </div>
                <p className="text-sm text-orange-800 leading-relaxed">
                  Deals, die heute gestartet werden, können rechnerisch nicht mehr in diesem Kalenderjahr abgeschlossen werden.
                  Ihre Jahres-Forecast-Basis sind ausschließlich Deals, die sich bereits jetzt aktiv im Prozess befinden.
                </p>
              </div>
            </div>
          )}

          {/* ── TRUST LAYER ── dezenter Vertrauensanker */}
          <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-border/60 bg-muted/30 px-5 py-3">
            <span className="text-xs text-muted-foreground font-medium">📊 Einordnung dieser Analyse:</span>
            <span className="text-xs text-muted-foreground">Abgeleitet aus Ihren Funnel- und Zielwerten</span>
            <span className="hidden sm:inline text-muted-foreground/30">·</span>
            <span className="text-xs text-muted-foreground">Abgeglichen mit B2B-Vertriebsbenchmarks (SaaS/Services)</span>
            <span className="hidden sm:inline text-muted-foreground/30">·</span>
            <span className="text-xs text-muted-foreground">Basis: Typische Muster aus vergleichbaren Sales-Teams</span>
          </div>

          <div className={`${card} p-8`}>
            <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-2">1. Ihre aktuelle Geschäftslage</h2>
              <p className="text-sm text-muted-foreground">
                Der Ausgangspunkt: Wo Sie heute stehen, wie groß die Lücke zum Jahresziel ist — und was passiert, wenn sich nichts ändert.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div className={softCard + " p-6 stripe-hover"}>
                <div className="flex items-center gap-2 text-muted-foreground text-sm font-semibold mb-3">
                  <Target className="h-5 w-5" />
                  Jahresziel Sales
                </div>
                <div className="text-3xl font-bold text-foreground">
                  {currency(teamPerformance?.totalRevenueTargetYear || 0)}
                </div>
                <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  Das definierte Umsatzziel für das laufende Kalenderjahr. Basis für alle Pace- und Forecast-Berechnungen.
                </div>
              </div>

              <div className={softCard + " p-6 stripe-hover"}>
                <div className="flex items-center gap-2 text-muted-foreground text-sm font-semibold mb-3">
                  <Activity className="h-5 w-5" />
                  Bisher erzielt
                </div>
                <div className="text-3xl font-bold text-foreground">
                  {currency(teamPerformance?.revenueAchievedYTD || 0)}
                </div>
                <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  Tatsächlich realisierter Umsatz seit Jahresbeginn (Year-to-Date). Zeigt den aktuellen Stand.
                </div>
              </div>

              <div className={softCard + " p-6 stripe-hover"}>
                <div className="flex items-center gap-2 text-muted-foreground text-sm font-semibold mb-3">
                  <Gauge className="h-5 w-5" />
                  Lineares Soll bis heute
                </div>
                <div className="text-3xl font-bold text-foreground">
                  {currency(teamPerformance?.expectedRevenueByNow || 0)}
                </div>
                <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  Erwarteter Umsatz bis heute bei gleichmäßigem Verlauf über das Jahr.
                </div>
              </div>

              <div className={softCard + " p-6 stripe-hover"}>
                <div className="flex items-center gap-2 text-destructive text-sm font-semibold mb-3">
                  <AlertTriangle className="h-5 w-5" />
                  Forecast-Gap
                </div>
                <div className="text-4xl font-bold text-destructive">
                  {currency(teamPerformance?.projectedYearEndGap || 0)}
                </div>
                <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  Voraussichtliche Ziellücke zum Jahresende — wenn die aktuelle Pace unverändert bleibt.
                </div>
                {computedMetrics?.forecastSignal?.isEarlyForecastLowConfidence && (
                  <div className="mt-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                    Frühe Tendenz: Die Hochrechnung ist noch wenig belastbar, weil bisher nur wenige Abschlüsse bzw. wenig Jahresverlauf vorliegen.
                    {normalizedData.salesCycleMonths > 0 && (
                      <span className="block mt-1">
                        Hinweis: Bei einem Sales Cycle von {normalizedData.salesCycleMonths} Monaten wirken sich heutige Pipeline-Probleme erst verzögert auf den Umsatz aus.
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8">
              <h3 className="text-lg font-semibold text-foreground mb-1">Wo Umsatz gerade liegen bleibt</h3>
              <p className="text-sm text-muted-foreground mb-5">
                {computedMetrics.revenueGapSource === "forecast-delta"
                  ? "Abgeleitet aus Ihrem Jahresumsatzziel und dem aktuellen Monatspfad. Diese Lücke zeigt, was monatlich fehlt, um das Jahresziel noch zu erreichen."
                  : "Abgeleitet aus dem Abgleich Ihrer aktuellen Abschlussquoten mit typischen Werten vergleichbarer B2B-Teams. Diese Zahlen zeigen, was bei stärkerer Execution realistisch erreichbar wäre."}
              </p>

              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                {/* 1. Monatliche Umsatzlücke */}
                <div className={softCard + " p-6 stripe-hover border-destructive/20"}>
                  <div className="flex items-center gap-2 text-destructive text-sm font-semibold mb-3">
                    <Target className="h-5 w-5" />
                    {computedMetrics.revenueGapSource === "forecast-delta"
                      ? "Monatlicher Zielrückstand"
                      : "Monatliche Umsatzlücke"}
                  </div>
                  <div className="text-4xl font-bold text-destructive tabular-nums">
                    {currency(computedMetrics.monthlyRevenueGap)}
                  </div>
                  <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    {computedMetrics.revenueGapSource === "forecast-delta"
                      ? `Abgeleitet aus Ihrem Jahresziel vs. aktuellem Monatspfad (${currency(computedMetrics.currentRevenue)}/Monat). Diese Zahl zeigt, was monatlich fehlt, um das Jahresziel noch zu erreichen.`
                      : `Der Abstand zwischen dem, was Sie heute erzielen (${currency(computedMetrics.currentRevenue)}/Monat), und dem, was mit besserer Execution und optimierten Conversion-Raten möglich wäre.`}
                  </div>
                  {computedMetrics.revenueGapSource === "forecast-delta" && (
                    <div className="mt-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-2 py-1.5">
                      ⚡ Basierend auf Zielabweichung — präzisere Berechnung mit Kontaktdaten möglich
                    </div>
                  )}
                </div>

                {/* 2. Jährliches Potenzial */}
                <div className={softCard + " p-6 stripe-hover border-accent/20"}>
                  <div className="flex items-center gap-2 text-accent text-sm font-semibold mb-3">
                    <TrendingUp className="h-5 w-5" />
                    {computedMetrics.revenueGapSource === "forecast-delta"
                      ? "Jährlicher Zielrückstand"
                      : "Jährliches Potenzial"}
                  </div>
                  <div className="text-4xl font-bold text-accent">
                    {currency(computedMetrics.annualRevenueGap)}
                  </div>
                  <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    {computedMetrics.revenueGapSource === "forecast-delta"
                      ? <>Die kumulierte Abweichung über das gesamte Jahr. Bei unveränderter Pace fehlen zum Ziel{" "}<strong className="text-destructive">{currency(computedMetrics.annualRevenueGap)}</strong>.</>
                      : <>Bei stärkerer Execution entspricht das{" "}
                          {normalizedData.averageDealSize > 0
                            ? `ca. ${num(computedMetrics.annualRevenueGap / normalizedData.averageDealSize, 0)} zusätzlichen Abschlüssen pro Jahr.`
                            : "mehreren zusätzlichen Abschlüssen pro Jahr."}
                        </>}
                  </div>
                </div>

                {/* 3. Forecast Jahresende */}
                <div className={softCard + " p-6 stripe-hover"}>
                  <div className="flex items-center gap-2 text-muted-foreground text-sm font-semibold mb-3">
                    <LineChart className="h-5 w-5" />
                    Forecast Jahresende
                  </div>
                  <div className="text-3xl font-bold text-foreground">
                    {currency(teamPerformance?.projectedYearEndRevenue || 0)}
                  </div>
                  <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    Bei unveränderter Pace. Ziel:{" "}
                    <span className={teamPerformance?.projectedYearEndGap && teamPerformance.projectedYearEndGap > 0 ? "text-destructive font-semibold" : "text-accent font-semibold"}>
                      {currency(teamPerformance?.totalRevenueTargetYear || 0)}
                    </span>
                    {teamPerformance?.projectedYearEndGap && teamPerformance.projectedYearEndGap > 0
                      ? ` — Lücke: ${currency(teamPerformance.projectedYearEndGap)}`
                      : " — auf Kurs"}
                  </div>
                </div>

                {/* 4. Größter Hebel */}
                <div className={softCard + " p-6 stripe-hover border-accent/20"}>
                  <div className="flex items-center gap-2 text-accent text-sm font-semibold mb-3">
                    <Zap className="h-5 w-5" />
                    Größter Hebel
                  </div>
                  <div className="text-lg font-bold text-foreground leading-tight">
                    {topLevers?.[0]?.title || "Hebel wird berechnet"}
                  </div>
                  {topLevers?.[0]?.monthlyImpact ? (
                    <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
                      <span className="text-accent font-semibold">{currency(topLevers[0].monthlyImpact)} / Monat</span> — {topLevers[0].whyItMatters || "mit direktem Umsatzbezug"}
                    </div>
                  ) : (
                    <div className="mt-3 text-sm text-muted-foreground">mit direktem Umsatzbezug</div>
                  )}
                </div>
              </div>

              {/* CAPACITY CHECK — Redesigned */}
              {computedMetrics?.capacityCheck && teamPerformance?.totalRevenueTargetYear > 0 && (() => {
                const cc = computedMetrics.capacityCheck;
                const target = teamPerformance.totalRevenueTargetYear;
                const optimized = cc.optimizedMaxRevenueCapacity;
                const current = cc.currentMaxRevenueCapacity;
                const gap = cc.capacityGap;
                const coveragePct = target > 0 ? Math.min(100, Math.round((optimized / target) * 100)) : 0;
                const isCritical = cc.isTargetUnreachableWithCurrentSetup;
                const isStrained = !isCritical && cc.isTargetUnreachableWithCurrentPerformance;
                const isOnTrack = !isCritical && !isStrained;

                return (
                  <div className={`mt-6 rounded-2xl border-2 overflow-hidden ${
                    isCritical ? "border-red-200" : isStrained ? "border-amber-200" : "border-emerald-200"
                  }`}>
                    {/* Header */}
                    <div className={`px-6 py-4 ${
                      isCritical ? "bg-red-600" : isStrained ? "bg-amber-500" : "bg-emerald-600"
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="rounded-full bg-white/20 p-2">
                            <Users className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <div className="text-white font-bold text-base">Setup-Kapazität vs. Umsatzziel</div>
                            <div className="text-white/70 text-xs mt-0.5">
                              {isCritical
                                ? "Strukturelle Kapazitätslücke erkannt"
                                : isStrained
                                ? "Ziel erreichbar — aber nur mit besserer Execution"
                                : "Setup ist grundsätzlich zielkonform"}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-white/70 text-xs">Kapazitätsabdeckung</div>
                          <div className="text-white font-black text-2xl tabular-nums">{coveragePct}%</div>
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <div className={`px-6 py-5 ${
                      isCritical ? "bg-red-50" : isStrained ? "bg-amber-50" : "bg-emerald-50"
                    }`}>

                      {/* Progress Bar */}
                      <div className="mb-5">
                        <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                          <span>Theoretische Jahreskapazität (optimiert)</span>
                          <span className="font-semibold">{coveragePct}% des Ziels</span>
                        </div>
                        <div className="h-3 rounded-full bg-black/10 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              isCritical ? "bg-red-500" : isStrained ? "bg-amber-500" : "bg-emerald-500"
                            }`}
                            style={{ width: `${coveragePct}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs mt-1.5">
                          <span className={`font-bold ${
                            isCritical ? "text-red-700" : isStrained ? "text-amber-700" : "text-emerald-700"
                          }`}>{currency(optimized)}</span>
                          <span className="text-muted-foreground font-medium">Ziel: {currency(target)}</span>
                        </div>
                      </div>

                      {/* 3-Spalten Zahlen */}
                      <div className="grid grid-cols-3 gap-4 mb-5">
                        <div className="rounded-xl bg-white/70 border border-black/5 p-4">
                          <div className="text-xs text-muted-foreground mb-1">Aktuelle Kapazität</div>
                          <div className="text-xl font-bold text-foreground tabular-nums">{currency(current)}</div>
                          <div className="text-xs text-muted-foreground mt-1">bei heutiger Performance</div>
                        </div>
                        <div className="rounded-xl bg-white/70 border border-black/5 p-4">
                          <div className="text-xs text-muted-foreground mb-1">Optimierte Kapazität</div>
                          <div className={`text-xl font-bold tabular-nums ${
                            isCritical ? "text-red-700" : isStrained ? "text-amber-700" : "text-emerald-700"
                          }`}>{currency(optimized)}</div>
                          <div className="text-xs text-muted-foreground mt-1">bei realistischer Optimierung</div>
                        </div>
                        <div className="rounded-xl bg-white/70 border border-black/5 p-4">
                          <div className="text-xs text-muted-foreground mb-1">
                            {isCritical ? "Strukturelle Lücke" : isStrained ? "Execution-Lücke" : "Puffer über Ziel"}
                          </div>
                          <div className={`text-xl font-bold tabular-nums ${
                            isCritical ? "text-red-700" : isStrained ? "text-amber-700" : "text-emerald-700"
                          }`}>
                            {isCritical || isStrained ? `− ${currency(gap)}` : `+ ${currency(Math.abs(gap))}`}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {isCritical ? "nicht durch Execution allein lösbar" : isStrained ? "durch Execution schließbar" : "Spielraum vorhanden"}
                          </div>
                        </div>
                      </div>

                      {/* Diagnose-Text */}
                      <div className={`rounded-xl border px-4 py-3 text-sm leading-relaxed ${
                        isCritical
                          ? "bg-red-100 border-red-200 text-red-900"
                          : isStrained
                          ? "bg-amber-100 border-amber-200 text-amber-900"
                          : "bg-emerald-100 border-emerald-200 text-emerald-900"
                      }`}>
                        {isCritical ? (
                          <>
                            <span className="font-bold">Strukturelle Kapazitätslücke:</span> Selbst wenn Ihr Team morgen perfekt performen würde — die rechnerische Jahreskapazität von{" "}
                            <strong>{currency(optimized)}</strong> reicht nicht aus, um das Ziel von{" "}
                            <strong>{currency(target)}</strong> zu erreichen. Die Lücke von{" "}
                            <strong>{currency(gap)}</strong> lässt sich nicht durch Execution allein schließen.
                            {" "}Das bedeutet: Entweder muss das Team wachsen, der Ø Deal-Wert steigen, oder das Ziel wird revidiert.
                          </>
                        ) : isStrained ? (
                          <>
                            <span className="font-bold">Execution-Lücke:</span> Das Ziel ist grundsätzlich erreichbar — aber aktuell deckt die Performance nur{" "}
                            <strong>{Math.round((current / target) * 100)}%</strong> des Ziels ab.
                            {" "}Mit optimierter Conversion und konsequenterer Pipeline-Steuerung steigt die Kapazität auf{" "}
                            <strong>{currency(optimized)}</strong>. Die fehlenden{" "}
                            <strong>{currency(gap)}</strong> sind kein Ressourcenproblem — sie sind ein Execution-Problem.
                          </>
                        ) : (
                          <>
                            <span className="font-bold">Setup ist zielkonform:</span> Die theoretische Kapazität Ihres Teams liegt über dem Jahresziel.
                            {" "}Die Herausforderung liegt nicht in der Teamgröße, sondern in Conversion-Konstanz, Pipeline-Qualität und sauberer Steuerung.
                            {" "}Wenn das Ziel dennoch nicht erreicht wird, liegt die Ursache in der Execution — nicht im Setup.
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* UNIT ECONOMICS */}
              {computedMetrics?.costInefficiency && computedMetrics.costInefficiency.totalSalaryCostYear > 0 && (
                <div className={`mt-4 rounded-xl border p-5 ${
                  computedMetrics.costInefficiency.revenuePerEuroSalary < 3
                    ? "border-red-300 bg-red-50"
                    : computedMetrics.costInefficiency.revenuePerEuroSalary < 5
                    ? "border-orange-300 bg-orange-50"
                    : "border-emerald-300 bg-emerald-50"
                }`}>
                  <div className={`flex items-center gap-2 font-semibold text-sm mb-3 ${
                    computedMetrics.costInefficiency.revenuePerEuroSalary < 3
                      ? "text-red-700"
                      : computedMetrics.costInefficiency.revenuePerEuroSalary < 5
                      ? "text-orange-700"
                      : "text-emerald-700"
                  }`}>
                    <AlertTriangle className="h-4 w-4" />
                    Unit Economics — Umsatz pro € Gehaltskosten
                  </div>
                  <div className="grid gap-4 md:grid-cols-3 text-sm mb-3">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Umsatz / € Gehalt</div>
                      <div className={`text-2xl font-bold ${
                        computedMetrics.costInefficiency.revenuePerEuroSalary < 3 ? "text-red-700"
                        : computedMetrics.costInefficiency.revenuePerEuroSalary < 5 ? "text-orange-700"
                        : "text-emerald-700"
                      }`}>
                        {num(computedMetrics.costInefficiency.revenuePerEuroSalary, 2)} €
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {computedMetrics.costInefficiency.salaryEfficiencyBand === "critical"
                          ? "🚨 Kritisch — klar unter Benchmark"
                          : computedMetrics.costInefficiency.salaryEfficiencyBand === "low"
                          ? "⚠️ Ausbaufähig — unter Ziel"
                          : computedMetrics.costInefficiency.salaryEfficiencyBand === "solid"
                          ? "✓ Solide — gutes Niveau"
                          : "🏆 Stark — überdurchschnittlich"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Gehaltskosten / Jahr</div>
                      <div className="text-2xl font-bold text-foreground">{currency(computedMetrics.costInefficiency.totalSalaryCostYear)}</div>
                      <div className="text-xs text-muted-foreground mt-1">inkl. aller produktiven Reps</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Effizienz-Ziel</div>
                      <div className="text-2xl font-bold text-foreground">≥ 6 €</div>
                      <div className="text-xs text-muted-foreground mt-1">B2B High-Performance Zielwert</div>
                    </div>
                  </div>
                  <div className="text-sm text-foreground leading-relaxed">
                    {computedMetrics.costInefficiency.showBudgetEfficiencyWarning ? (
                      <>
                        Bei Ihrer aktuellen Pace wird ein Teil des Sales-Budgets noch nicht voll wirksam in Zielerreichung übersetzt.
                        Pro Euro Gehaltskosten generiert Ihr Team aktuell{" "}
                        <strong className={computedMetrics.costInefficiency.salaryEfficiencyBand === "critical" ? "text-red-700" : "text-orange-700"}>
                          {num(computedMetrics.costInefficiency.revenuePerEuroSalary, 2)} €
                        </strong>{" "}
                        Umsatz — Stufe:{" "}
                        <strong>
                          {computedMetrics.costInefficiency.salaryEfficiencyBand === "critical" ? "kritisch" :
                           computedMetrics.costInefficiency.salaryEfficiencyBand === "low" ? "ausbaufähig" :
                           computedMetrics.costInefficiency.salaryEfficiencyBand === "solid" ? "solide" : "stark"}
                        </strong>.
                        {computedMetrics.costInefficiency.revenueEfficiencyGapAnnual > 0 && (
                          <> Gegenüber einem starken Effizienzniveau liegt rechnerisch ein Potenzial von{" "}
                            <strong className="text-accent">{currency(computedMetrics.costInefficiency.revenueEfficiencyGapAnnual)}</strong>{" "}
                            pro Jahr offen.
                          </>
                        )}
                      </>
                    ) : (
                      <>Pro Euro Gehaltskosten generiert Ihr Team aktuell{" "}
                        <strong className="text-emerald-700">{num(computedMetrics.costInefficiency.revenuePerEuroSalary, 2)} €</strong>{" "}
                        Umsatz — ein {computedMetrics.costInefficiency.salaryEfficiencyBand === "strong" ? "starkes" : "solides"} Verhältnis.
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* AKQUISE-MIX BLOCK entfernt — nur in Handlungsempfehlung */}
        {false && sourceMix.hasData && (
          <section className={`${card} mb-8 p-6`}>
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-accent/10 text-accent uppercase tracking-wider">
                  <Activity className="h-3 w-3" /> Akquise-Mix
                </span>
                {/* Risiko-Badge */}
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                  sourceMix.riskLevel === 'high' ? 'bg-destructive/10 text-destructive' :
                  sourceMix.riskLevel === 'medium' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                  'bg-emerald-50 text-emerald-700 border border-emerald-200'
                }`}>
                  {sourceMix.riskLevel === 'high' ? '⚠ Klumpenrisiko' : sourceMix.riskLevel === 'medium' ? '~ Optimierungspotenzial' : '✓ Solider Mix'}
                </span>
              </div>
              <h2 className={sectionTitle}>Wie Sie Neukunden gewinnen — und was das bedeutet</h2>
              <p className={sectionSub}>
                Jeder Akquisekanal hat ein anderes Risikoprofil, eine andere Skalierbarkeit und beeinflusst Ihren Sales Cycle unterschiedlich.
              </p>
            </div>

            {/* Strategische Bewertung — prominent oben */}
            <div className={`rounded-xl border px-5 py-4 mb-6 text-sm leading-relaxed ${
              sourceMix.riskLevel === 'high' ? 'border-destructive/30 bg-destructive/5 text-foreground' :
              sourceMix.riskLevel === 'medium' ? 'border-amber-200 bg-amber-50/60 text-foreground' :
              'border-emerald-200 bg-emerald-50/60 text-foreground'
            }`}>
              <p className="font-semibold mb-1 text-foreground">
                {sourceMix.riskLevel === 'high' ? 'Strukturelles Risiko erkannt' :
                 sourceMix.riskLevel === 'medium' ? 'Strategische Einordnung' :
                 'Mix-Bewertung'}
              </p>
              <p className="text-muted-foreground">{sourceMix.strategicNote}</p>
            </div>

            {/* Kanal-Karten mit echten Insights */}
            <div className="grid gap-3 sm:grid-cols-2">
              {sourceMix.insights.map((ins, idx) => {
                const isTop = idx === 0;
                const barWidth = Math.min(100, Math.max(4, ins.value));
                return (
                  <div
                    key={ins.key}
                    className={`rounded-xl border p-4 ${
                      isTop ? 'border-accent/40 bg-accent/5' : 'border-border bg-muted/20'
                    }`}
                  >
                    {/* Kanal-Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${ isTop ? 'text-accent' : 'text-foreground'}`}>{ins.label}</span>
                        {isTop && <span className="text-[10px] font-semibold uppercase tracking-wider text-accent bg-accent/10 rounded-full px-2 py-0.5">Hauptkanal</span>}
                      </div>
                      <span className={`text-lg font-black tabular-nums ${ isTop ? 'text-accent' : 'text-muted-foreground'}`}>{ins.value}%</span>
                    </div>

                    {/* Balken */}
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-4">
                      <div
                        className={`h-full rounded-full ${ isTop ? 'bg-accent' : 'bg-muted-foreground/50'}`}
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>

                    {/* Insight-Zeilen */}
                    <div className="space-y-2 text-xs">
                      <div className="flex gap-2">
                        <span className="w-24 shrink-0 text-muted-foreground font-medium">Skalierbarkeit</span>
                        <span className="text-foreground font-semibold">{ins.scalability}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="w-24 shrink-0 text-muted-foreground font-medium">Sales Cycle</span>
                        <span className="text-foreground">{ins.cycleImpact}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="w-24 shrink-0 text-muted-foreground font-medium">Conversion</span>
                        <span className="text-foreground">{ins.conversionProfile}</span>
                      </div>
                      {ins.riskNote && (
                        <div className="flex gap-2 pt-1 border-t border-border/50">
                          <span className="w-24 shrink-0 text-muted-foreground font-medium">Risiko</span>
                          <span className="text-amber-700">{ins.riskNote}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Wenn mehr als 4 Kanäle: Rest kompakt anzeigen */}
            {sourceMix.shares.length > 4 && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Weitere Kanäle:</p>
                <div className="flex flex-wrap gap-2">
                  {sourceMix.shares.slice(4).map(s => (
                    <span key={s.key} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/30 px-3 py-1 text-xs text-muted-foreground">
                      {s.label} <span className="font-bold text-foreground">{s.value}%</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* 1. STRUKTURELLE URSACHEN */}
        {structuralCauses && structuralCauses.length > 0 && (
          <section id="section-strukturelle-ursachen" className={`${card} mb-8 p-6`}>
            <div className="mb-6">
              <h2 className={sectionTitle}>2. Die Ursachen hinter Ihrer Ziellücke</h2>
              <p className={sectionSub}>
                Diese Faktoren bremsen Ihre Zielerreichung strukturell aus — mit konkretem Einfluss auf Umsatz, Pipeline und Abschlussrate.
              </p>
            </div>
            <div className="space-y-4">
              {structuralCauses.map((cause, idx) => (
                <div
                  key={cause.id}
                  className={cn(
                    "rounded-2xl border p-5",
                    cause.severity === 'high' && "border-destructive/30 bg-destructive/5",
                    cause.severity === 'medium' && "border-amber-400/30 bg-amber-50/40 dark:bg-amber-900/10",
                    cause.severity === 'good' && "border-emerald-400/30 bg-emerald-50/40 dark:bg-emerald-900/10"
                  )}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <span className={cn(
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white",
                        cause.severity === 'high' ? "bg-destructive" : cause.severity === 'medium' ? "bg-amber-500" : "bg-emerald-500"
                      )}>
                        {idx + 1}
                      </span>
                      <h3 className="text-base font-semibold text-foreground">{cause.title}</h3>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <SeverityBadge severity={cause.severity} />
                      {cause.revenueImpactMonthly !== undefined && cause.revenueImpactMonthly > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
                          ~{currency(cause.revenueImpactMonthly)}/Monat Potenzial
                        </span>
                      )}
                    </div>
                  </div>

                  {cause.metricLabel && cause.metricValue && (
                    <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-background/60 border border-border px-3 py-1.5">
                      <span className="text-xs text-muted-foreground">{cause.metricLabel}:</span>
                      <span className="text-xs font-semibold text-foreground">{cause.metricValue}</span>
                    </div>
                  )}

                  <p className="mt-3 text-sm text-foreground/80">{cause.summary}</p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">Geschäftliche Folge:</span> {cause.businessImpact}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SALES TEAM PACE & ZIELERREICHUNG */}
        {teamPerformance && teamPerformance.productiveReps > 0 && (
          <section id="section-team-pace" className={`${card} mb-8 p-6`}>
            <div className="mb-5">
              <h2 className={sectionTitle}>3. Wo Ihr Team heute steht — und was das bedeutet</h2>
              <p className={sectionSub}>
                Der Abstand zwischen Ihrer aktuellen Pace und dem Jahresziel — und was daraus für die verbleibenden Monate folgt.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div className={softCard + " p-5"}>
                <div className="text-sm text-muted-foreground mb-2">Produktive Reps</div>
                <div className="text-2xl font-bold text-foreground">{teamPerformance.productiveReps}</div>
                <div className="mt-2 text-xs text-muted-foreground">Anzahl aktiver Sales Reps im Team</div>
              </div>

              <div className={softCard + " p-5"}>
                <div className="text-sm text-muted-foreground mb-2">Absolute Zielerreichung</div>
                <div className="text-2xl font-bold text-foreground">{pct(teamPerformance.absoluteGoalAttainmentPct)}</div>
                <div className="mt-2 text-xs text-muted-foreground">Bisher erzielt / Jahresziel (in %)</div>
              </div>

              <div className={softCard + " p-5"}>
                <div className="text-sm text-muted-foreground mb-2">Pace vs. Plan</div>
                <div className="text-2xl font-bold text-foreground">{pct(teamPerformance.pacingVsPlanPct)}</div>
                <div className="mt-2 text-xs text-muted-foreground">Bisher erzielt / lineares Soll (in %)</div>
              </div>

              <div className={softCard + " p-5"}>
                <div className="text-sm text-muted-foreground mb-2">Rep Health Score</div>
                <div className="text-2xl font-bold text-foreground">{teamPerformance.repHealthScore}/100</div>
                <div className="mt-2 text-xs text-muted-foreground">Gesamtbewertung: Zielsteuerung, Pipeline, Execution</div>
              </div>
            </div>

            <div className="mt-6 grid gap-6 md:grid-cols-3">
              <div className={softCard + " p-5"}>
                <div className="text-sm text-muted-foreground mb-2">Zielrückstand bis heute</div>
                <div className="text-2xl font-bold text-destructive">{currency(teamPerformance.paceGapToDate)}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Differenz zwischen linearem Soll und bisher erreichtem Umsatz. Zeigt den aktuellen Rückstand.
                </div>
              </div>

              <div className={softCard + " p-5"}>
                <div className="text-sm text-muted-foreground mb-2">Erforderlicher monatlicher Aufholbedarf</div>
                <div className="text-2xl font-bold text-foreground">{currency(teamPerformance.pacingRecoveryPotentialMonthly)}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Monatlich nötiger Mehrumsatz, um den Rückstand bis Jahresende aufzuholen und das Ziel zu erreichen.
                </div>
              </div>

              <div className={softCard + " p-5"}>
                <div className="text-sm text-muted-foreground mb-2">Projizierter Jahresumsatz</div>
                <div className="text-2xl font-bold text-foreground">{currency(teamPerformance.projectedYearEndRevenue)}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Hochgerechneter Jahresumsatz bei unveränderter Pace. Basis für Forecast-Gap.
                </div>
              </div>
            </div>

            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <div className={softCard + " p-5"}>
                <div className="text-sm text-muted-foreground mb-2">Ziel pro Rep / Jahr</div>
                <div className="text-2xl font-bold text-foreground">{currency(teamPerformance.targetPerRepYear)}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  ≈ {currency(teamPerformance.targetPerRepMonth)} pro Monat. Durchschnittliches Jahresziel je Rep.
                </div>
              </div>

              <div className={softCard + " p-5"}>
                <div className="text-sm text-muted-foreground mb-2">Ist pro Rep / Jahr bisher</div>
                <div className="text-2xl font-bold text-foreground">{currency(teamPerformance.actualRevenuePerRepYTD)}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Hochgerechnete Pace: ≈ {currency(teamPerformance.actualRevenuePerRepMonthPaced)} pro Monat
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
              <div className="text-sm font-semibold text-foreground mb-3">Was das für Ihr Geschäft bedeutet</div>
              <div className="space-y-2 text-sm text-foreground">
                {teamPerformance.summarySignals.hasPacingProblem && (
                  <p>⚠️ Das Team liegt unter dem linearen Sollverlauf. Ohne Gegensteuerung wird das Jahresziel voraussichtlich verfehlt.</p>
                )}
                {teamPerformance.summarySignals.hasTargetBreakdownProblem && (
                  <p>⚠️ Das Umsatzziel ist nicht konsequent auf Reps heruntergebrochen. Das erschwert Steuerung und Review.</p>
                )}
                {teamPerformance.summarySignals.hasPipelineProblem && (
                  <p>⚠️ Ein Teil der Reps verfügt nicht über ausreichend belastbare Pipeline. Dadurch entsteht ein strukturelles Risiko.</p>
                )}
                {teamPerformance.summarySignals.hasVarianceProblem && (
                  <p>⚠️ Die Leistungsstreuung im Team ist hoch. Standardisierung von Top-Performer-Verhalten hebt den Durchschnitt.</p>
                )}
                {teamPerformance.summarySignals.hasReviewProblem && (
                  <p>⚠️ Review-Rhythmus oder Steuerungsdisziplin deuten auf ein Management-Thema hin.</p>
                )}
              </div>
            </div>

            {/* ══ KONTAKT-AKTIVITÄT BENCHMARK ══ */}
            {teamPerformance.contactActivitySignal && teamPerformance.contactActivitySignal.level !== "unknown" && (
              <div className={`mt-6 rounded-xl p-5 border ${
                teamPerformance.contactActivitySignal.level === "critical"
                  ? "border-red-300 bg-red-50"
                  : teamPerformance.contactActivitySignal.level === "low"
                  ? "border-orange-300 bg-orange-50"
                  : teamPerformance.contactActivitySignal.level === "ok"
                  ? "border-yellow-200 bg-yellow-50"
                  : "border-emerald-200 bg-emerald-50"
              }`}>
                <div className={`flex items-center gap-2 text-sm font-bold mb-3 ${
                  teamPerformance.contactActivitySignal.level === "critical" ? "text-red-800" :
                  teamPerformance.contactActivitySignal.level === "low" ? "text-orange-800" :
                  teamPerformance.contactActivitySignal.level === "ok" ? "text-yellow-800" : "text-emerald-800"
                }`}>
                  <Activity className="h-4 w-4" />
                  Kontakt-Aktivität pro Rep
                  <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${
                    teamPerformance.contactActivitySignal.level === "critical" ? "bg-red-200 text-red-800" :
                    teamPerformance.contactActivitySignal.level === "low" ? "bg-orange-200 text-orange-800" :
                    teamPerformance.contactActivitySignal.level === "ok" ? "bg-yellow-200 text-yellow-800" : "bg-emerald-200 text-emerald-800"
                  }`}>
                    {teamPerformance.contactActivitySignal.level === "critical" ? "🚨 Kritisch" :
                     teamPerformance.contactActivitySignal.level === "low" ? "⚠️ Unter Benchmark" :
                     teamPerformance.contactActivitySignal.level === "ok" ? "✓ Akzeptabel" : "🏆 Top-Performer"}
                  </span>
                </div>
                <div className="grid gap-4 md:grid-cols-3 text-sm mb-4">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Ist / Rep / Monat</div>
                    <div className={`text-2xl font-bold ${
                      teamPerformance.contactActivitySignal.level === "critical" ? "text-red-700" :
                      teamPerformance.contactActivitySignal.level === "low" ? "text-orange-700" : "text-foreground"
                    }`}>
                      {teamPerformance.contactActivitySignal.contactsPerRepPerMonth}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Benchmark Minimum</div>
                    <div className="text-2xl font-bold text-foreground">{teamPerformance.contactActivitySignal.benchmarkMin}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Top-Performer</div>
                    <div className="text-2xl font-bold text-foreground">{teamPerformance.contactActivitySignal.benchmarkTop}</div>
                  </div>
                </div>
                {teamPerformance.contactActivitySignal.isCritical && (
                  <>
                    <div className={`text-sm font-semibold mb-1 ${
                      teamPerformance.contactActivitySignal.level === "critical" ? "text-red-800" : "text-orange-800"
                    }`}>
                      {teamPerformance.contactActivitySignal.level === "critical"
                        ? `🚨 Massiv unterperformant: ${teamPerformance.contactActivitySignal.gap} Kontakte/Rep/Monat fehlen zum Mindest-Benchmark.`
                        : `⚠️ ${teamPerformance.contactActivitySignal.gap} Kontakte/Rep/Monat unter Benchmark-Minimum.`}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Fehlende Aktivität im Team ({teamPerformance.contactActivitySignal.gapPerTeam} Kontakte/Monat) kostet
                      schätzungsweise{" "}
                      <span className={`font-semibold ${
                        teamPerformance.contactActivitySignal.level === "critical" ? "text-red-700" : "text-orange-700"
                      }`}>
                        {currency(teamPerformance.contactActivitySignal.monthlyRevenueLoss)}/Monat
                      </span>{" "}
                      ({currency(teamPerformance.contactActivitySignal.annualRevenueLoss)}/Jahr) an nicht realisiertem Umsatz.
                    </div>
                    <div className="mt-3 rounded-lg border border-current/20 p-3 text-xs text-muted-foreground">
                      <strong>Benchmark-Quelle:</strong> B2B Outbound Benchmark (SaaS/Services, Ø Team 3–15 Reps):{" "}
                      &lt;40 Kontakte/Mo = kritisch | 40–59 = unter Erwartung | 60–99 = akzeptabel | ≥100 = Top-Performer
                    </div>
                  </>
                )}
              </div>
            )}
          </section>
        )}

        {/* ── ZIELPFAD ABGESPECKT + REP EXECUTION TEASER + FOKUSFELDER LIGHT ── */}
        {goalPath && goalPath.remainingRevenueNeeded > 0 && (
          <section id="section-zielpfad" className={`${card} mb-8 p-6`}>
            <div className="mb-6">
              <h2 className={sectionTitle}>4. Was sich grundsätzlich ändern muss</h2>
              <p className={sectionSub}>
                Nicht als vollständiger Umsetzungsplan — sondern als klares Bild davon, was das aktuelle System liefern muss, damit das Ziel erreichbar bleibt.
              </p>
            </div>

            {/* Zielpfad — kompakte Kerninformation */}
            <div className="grid gap-4 md:grid-cols-3 mb-6">
              <div className={softCard + " p-5"}>
                <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-2">Noch benötigter Umsatz</div>
                <div className="text-2xl font-bold text-destructive">{currency(goalPath.remainingRevenueNeeded)}</div>
                <div className="mt-1.5 text-xs text-muted-foreground">Noch bis Jahresende erforderlich</div>
              </div>
              <div className={softCard + " p-5"}>
                <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-2">Nötige monatliche Leistung</div>
                <div className="text-2xl font-bold text-foreground">{currency(goalPath.requiredMonthlyRevenueFromNow)}</div>
                <div className="mt-1.5 text-xs text-muted-foreground">Ab sofort, um das Ziel zu erreichen</div>
              </div>
              <div className={softCard + " p-5"}>
                <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-2">Aktuell erzielt / Monat</div>
                <div className="text-2xl font-bold text-foreground">{currency(computedMetrics.currentRevenue)}</div>
                <div className="mt-1.5 text-xs text-muted-foreground">Pace vs. Ziel: {goalPath.requiredMonthlyRevenueFromNow > 0 ? pct((computedMetrics.currentRevenue / goalPath.requiredMonthlyRevenueFromNow) * 100, 0) : "–"}</div>
              </div>
            </div>

            {/* Rep Execution — nur Teaser */}
            {teamPerformance && teamPerformance.productiveReps > 0 && (
              <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/50 p-5">
                <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-2">
                  <Users className="h-4 w-4" />
                  Das aktuelle Rep-Setup reicht rechnerisch nicht aus
                </div>
                <p className="text-sm text-amber-800 leading-relaxed">
                  Bei {teamPerformance.productiveReps} Rep{teamPerformance.productiveReps > 1 ? "s" : ""} und der aktuellen Pace
                  {goalPath.requiredMonthlyRevenueFromNow > computedMetrics.currentRevenue && (
                    <> fehlt eine monatliche Mehrleistung von <strong>{currency(goalPath.requiredMonthlyRevenueFromNow - computedMetrics.currentRevenue)}</strong>
                    {" "}— das entspricht{" "}
                    <strong>{currency((goalPath.requiredMonthlyRevenueFromNow - computedMetrics.currentRevenue) / Math.max(1, teamPerformance.productiveReps))}/Rep/Monat</strong>
                    .</>
                  )}
                  {" "}Die konkrete Herleitung — was jeder Rep liefern muss und welche Aktivitäten das erfordert — ist Teil der Handlungsempfehlung.
                </p>
              </div>
            )}

            {/* Fokusfelder — Light: nur Richtung */}
            {result.focusPriorities && result.focusPriorities.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Wo zuerst ansetzen</div>
                <div className="space-y-2">
                  {result.focusPriorities.slice(0, 3).map((fp, i) => (
                    <div key={fp.id} className={`flex items-start gap-3 rounded-xl p-4 ${
                      i === 0 ? "border border-primary/20 bg-primary/5" : "border border-border bg-card"
                    }`}>
                      <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${
                        i === 0 ? "bg-primary" : "bg-muted-foreground/50"
                      }`}>{i + 1}</div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-foreground">{fp.title}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{fp.summary}</div>
                      </div>
                      {i === 0 && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 rounded-full px-2 py-0.5 shrink-0">Jetzt starten</span>
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-muted-foreground italic">Die konkreten Maßnahmen und die 30/60/90-Tage-Logik dahinter sind Teil der personalisierten Handlungsempfehlung.</p>
              </div>
            )}
          </section>
        )}

        {/* 3. OPERATIVE AUDIT-KPIs — ENTFERNT (zu tief für Diagnose) */}
        {false && goalPath && (
          <section className={`${card} mb-8 p-6`}>
            <div className="mb-5">
              <h2 className={sectionTitle}>Wie belastbar Ihr Vertriebsprozess wirklich ist</h2>
              <p className={sectionSub}>
                Vier Kennzahlen, die zeigen, wie gut qualifiziert, verbindlich und steuerbar Ihr aktueller Funnel tatsächlich ist.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className={softCard + " p-4"}>
                <div className="text-xs text-muted-foreground mb-1">Qualifizierte Leads</div>
                <div className="text-2xl font-bold text-foreground">
                  {qualifiedLeadRate !== null ? pct(qualifiedLeadRate, 0) : "–"}
                </div>
                <div className="text-xs text-muted-foreground mt-1">Anteil ICP-passender Leads am Gesamteingang</div>
              </div>
              <div className={softCard + " p-4"}>
                <div className="text-xs text-muted-foreground mb-1">Angebote mit Next Step</div>
                <div className="text-2xl font-bold text-foreground">
                  {proposalsWithNextStep !== null ? pct(proposalsWithNextStep, 0) : "–"}
                </div>
                <div className="text-xs text-muted-foreground mt-1">Verbindlichkeit in Spätphasen des Funnels</div>
              </div>
              <div className={softCard + " p-4"}>
                <div className="text-xs text-muted-foreground mb-1">Stagnierende Pipeline</div>
                <div className="text-2xl font-bold text-foreground">
                  {stalePipelineShare !== null ? pct(stalePipelineShare, 0) : "–"}
                </div>
                <div className="text-xs text-muted-foreground mt-1">Deals ohne echte Bewegung (&gt;14 Tage)</div>
              </div>
              <div className={softCard + " p-4"}>
                <div className="text-xs text-muted-foreground mb-1">Entscheider im Erstgespräch</div>
                <div className="text-2xl font-bold text-foreground">
                  {decisionMakerInFirstMeeting !== null ? pct(decisionMakerInFirstMeeting, 0) : "–"}
                </div>
                <div className="text-xs text-muted-foreground mt-1">Frühe Buying-Center-Einbindung</div>
              </div>
            </div>
          </section>
        )}

        {/* CONVERSION-QUALITÄT MINI-STRIP — entfernt: zu granular für Diagnose */}
        {false && computedMetrics.goalPath && computedMetrics.goalPath.remainingRevenueNeeded > 0 && (
          <section className="mb-6 overflow-hidden rounded-2xl border border-border bg-muted/20">
            <div className="grid divide-y md:divide-y-0 md:divide-x divide-border md:grid-cols-3">
              {/* C2D */}
              {(() => {
                const ist = computedMetrics.convContactToDiscovery;
                const adj = computedMetrics.goalPath!.adjustedC2D ?? ist;
                const ok = ist >= computedMetrics.benchmarkContactToDiscovery * 0.85;
                return (
                  <div className="flex flex-col gap-1 p-4">
                    <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Kontakt → Discovery</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className={`text-2xl font-bold ${ok ? "text-emerald-600" : "text-amber-600"}`}>{pct(ist)}</span>
                      <span className="text-xs text-muted-foreground">aktuell</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {ok ? "✓ solide" : "⚠ Lücke"}
                      </span>
                      <span className="text-xs text-muted-foreground">Soll-Basis: {pct(adj)}</span>
                    </div>
                  </div>
                );
              })()}
              {/* D2P */}
              {(() => {
                const ist = computedMetrics.convDiscoveryToPresentation;
                const adj = computedMetrics.goalPath!.adjustedD2P ?? ist;
                const ok = ist >= computedMetrics.benchmarkDiscoveryToPresentation * 0.85;
                return (
                  <div className="flex flex-col gap-1 p-4">
                    <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Discovery → Präsentation</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className={`text-2xl font-bold ${ok ? "text-emerald-600" : "text-amber-600"}`}>{pct(ist)}</span>
                      <span className="text-xs text-muted-foreground">aktuell</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {ok ? "✓ solide" : "⚠ Lücke"}
                      </span>
                      <span className="text-xs text-muted-foreground">Soll-Basis: {pct(adj)}</span>
                    </div>
                  </div>
                );
              })()}
              {/* P2D */}
              {(() => {
                const ist = computedMetrics.convPresentationToDeal;
                const adj = computedMetrics.goalPath!.adjustedP2D ?? ist;
                const ok = ist >= computedMetrics.benchmarkPresentationToDeal * 0.85;
                return (
                  <div className="flex flex-col gap-1 p-4">
                    <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Präsentation → Deal</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className={`text-2xl font-bold ${ok ? "text-emerald-600" : "text-red-600"}`}>{pct(ist)}</span>
                      <span className="text-xs text-muted-foreground">aktuell</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {ok ? "✓ solide" : "✗ krit. Lücke"}
                      </span>
                      <span className="text-xs text-muted-foreground">Soll-Basis: {pct(adj)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </section>
        )}

        {/* ── PREDICTIVE INSIGHT / SIGNATURE MOMENT ── */}
        {goalPath && goalPath.remainingRevenueNeeded > 0 && teamPerformance?.summarySignals?.hasPacingProblem && (
          <section className="mb-8">
            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-bold text-amber-900 mb-1">Was passiert, wenn sich nichts ändert</div>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    Mit Ihrer aktuellen Pace reicht die bestehende Pipeline rechnerisch nicht aus, um das Jahresziel stabil zu erreichen.
                    {goalPath.remainingRevenueNeeded > 0 && (
                      <> Der verbleibende Umsatzbedarf von <strong>{currency(goalPath.remainingRevenueNeeded)}</strong> erfordert eine monatliche Leistung von <strong>{currency(goalPath.requiredMonthlyRevenueFromNow)}</strong> — deutlich über dem aktuellen Niveau.
                      </>
                    )}
                  </p>
                  <p className="mt-2 text-sm text-amber-700 leading-relaxed">
                    Die aktuelle Zielabweichung lässt sich nicht durch einzelne Großabschlüsse lösen — sondern nur durch eine strukturelle Verbesserung im Vertriebssystem.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* FUNNEL DIAGNOSE — entfernt: zu operativ für Diagnose-Ebene */}
        {false && computedMetrics?.funnelDiagnosis && computedMetrics.goalPath?.requiredContactsPerMonthFromNow > 0 && (
          <section className={`${card} mb-8 p-6`}>
            <div className="mb-5">
              <h2 className={sectionTitle}>Wo Ihr Funnel das Ziel blockiert</h2>
              <p className={sectionSub}>
                An welchen Stellen im Prozess die Lücke zum Zielpfad entsteht — mit konkreten Zahlen je Stufe.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {computedMetrics.funnelDiagnosis.stages.map((stage, i) => {
                const gapPositive = stage.gap > 0;
                const blocking = stage.isBlocking;
                return (
                  <div key={i} className={softCard + " p-5 flex flex-col gap-3"}>
                    <div className="text-sm font-semibold text-foreground border-b border-border pb-2">
                      {stage.name}
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Aktuell</div>
                        <div className="text-xl font-bold text-foreground">{pct(stage.actual)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Erforderlich</div>
                        <div className={`text-xl font-bold ${gapPositive ? "text-amber-600" : "text-emerald-600"}`}>
                          {stage.required > 0 ? pct(stage.required) : "–"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Potenzial</div>
                        <div className="text-xl font-bold text-blue-600">{pct(stage.potential)}</div>
                      </div>
                    </div>
                    <div className="mt-auto pt-2 border-t border-border text-sm">
                      {blocking ? (
                        <span className="flex items-center gap-1.5 text-red-600 font-medium">
                          <span>✗</span> Selbst bei Optimierung unzureichend
                        </span>
                      ) : gapPositive ? (
                        <span className="flex items-center gap-1.5 text-amber-600 font-medium">
                          <span>⚠</span> Verbesserung notwendig (+{pct(stage.gap)})
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
                          <span>✓</span> Ausreichend
                        </span>
                      )}
                      <div className="mt-2 text-xs text-muted-foreground">
                        {blocking
                          ? "Selbst bei Optimierung reicht diese Stufe aktuell nicht aus."
                          : gapPositive
                          ? "Diese Stufe muss verbessert werden, um das Ziel zu erreichen."
                          : "Diese Stufe liegt aktuell im notwendigen Bereich."}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Gesamtbewertung Funnel */}
            <div className="mt-6 rounded-xl border border-red-200 bg-red-50/60 p-5">
              <div className="text-sm font-semibold text-foreground mb-2">Gesamtbild: Was das für Ihre Zielerreichung bedeutet</div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {computedMetrics.funnelDiagnosis.stages.some((s) => s.isBlocking)
                  ? "Ihr aktueller Funnel ist strukturell nicht in der Lage, das gesetzte Umsatzziel zu erreichen – selbst bei Optimierung einzelner Stufen."
                  : "Ihr Funnel kann das Ziel grundsätzlich erreichen, erfordert jedoch eine konsequente Verbesserung in einzelnen Stufen."}
              </p>
            </div>

            {/* Datenqualität */}
            {computedMetrics.dataQuality && (
              <div className="mt-5 rounded-xl border border-border bg-muted/30 p-5">
                <div className="flex flex-wrap items-center gap-4 mb-3">
                  <div className="text-sm font-semibold text-foreground">Ihre Funnel-Steuerung ist aktuell nur eingeschränkt belastbar</div>
                  <div className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full ${
                    computedMetrics.dataQuality.level === "high"
                      ? "bg-emerald-100 text-emerald-700"
                      : computedMetrics.dataQuality.level === "medium"
                      ? "bg-amber-100 text-amber-700"
                      : "bg-red-100 text-red-700"
                  }`}>
                    <span className="text-base">
                      {computedMetrics.dataQuality.level === "high" ? "●" :
                       computedMetrics.dataQuality.level === "medium" ? "●" : "●"}
                    </span>
                    Score: {computedMetrics.dataQuality.score} –{" "}
                    {computedMetrics.dataQuality.level === "high" ? "belastbar" :
                     computedMetrics.dataQuality.level === "medium" ? "teilweise" : "kritisch"}
                  </div>
                </div>
                {computedMetrics.dataQuality.missingFields.length > 0 ? (
                  <>
                    <p className="text-sm text-muted-foreground mb-3">
                      Ein Teil Ihrer Funnel-Daten ist aktuell nicht vollständig oder nicht belastbar erfasst.
                      Das bedeutet: Wichtige Entscheidungen basieren teilweise auf Annahmen statt auf klaren Zahlen.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {computedMetrics.dataQuality.missingFields.map((f, i) => (
                        <span key={i} className="rounded-full bg-red-100 text-red-700 text-xs px-3 py-1 font-medium">
                          {f}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-red-700 font-medium">
                      Dadurch ist aktuell nicht eindeutig erkennbar, ob Ihr Hauptproblem im Volumen,
                      in der Conversion oder in der Deal-Qualität liegt.
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Schon kleine Abweichungen in diesen Daten können Ihre Zielerreichung signifikant beeinflussen.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Alle Kerndaten vorhanden — die Analyse ist vollständig belastbar.
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        {/* BENCHMARK — kompakter Pill-Strip: Ihr Wert vs. B2B-Benchmark */}
        <section className="mb-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-1">Ihr Funnel vs. B2B-Benchmark:</span>
            {([
              {
                label: "Kontakt → Discovery",
                actual: computedMetrics.convContactToDiscovery,
                benchmark: computedMetrics.benchmarkContactToDiscovery,
              },
              {
                label: "Discovery → Präsentation",
                actual: computedMetrics.convDiscoveryToPresentation,
                benchmark: computedMetrics.benchmarkDiscoveryToPresentation,
              },
              {
                label: "Präsentation → Deal",
                actual: computedMetrics.convPresentationToDeal,
                benchmark: computedMetrics.benchmarkPresentationToDeal,
              },
            ] as { label: string; actual: number; benchmark: number }[]).map((row, i) => {
              const ok = row.actual >= row.benchmark * 0.85;
              return (
                <div
                  key={i}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium ${
                    ok
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-red-200 bg-red-50 text-red-700"
                  }`}
                >
                  <span>{ok ? "✓" : "⚠"}</span>
                  <span className="font-semibold">{row.label}:</span>
                  <span>{pct(row.actual)}</span>
                  <span className="text-muted-foreground opacity-60">/ {pct(row.benchmark)}</span>
                </div>
              );
            })}
            <span className="text-[10px] text-muted-foreground ml-1">B2B SaaS/Services Benchmark-Werte</span>
          </div>
          {/* Wettbewerbliche Kontextkarte */}
          <div className="mt-4 rounded-xl border border-border bg-card px-5 py-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              <span className="font-semibold text-foreground">Was das bedeutet:</span>{" "}
              Teams, die in mehreren Conversion-Stufen unter Benchmark liegen, müssen für dasselbe Umsatzziel deutlich mehr investieren — in Leads, Zeit und Ressourcen. Mehr Volumen löst ein Conversion-Problem nicht. Es verstärkt es.
            </p>
          </div>
        </section>

        {/* 3 SZENARIEN-VERGLEICH — entfernt */}
        {false && scenarios && (
          <section className={`${card} mb-8 p-6`}>
            <div className="mb-5">
              <h2 className={sectionTitle}>Drei Wachstumsszenarien</h2>
              <p className={sectionSub}>
                Basierend auf Ihren Daten zeigen wir drei realistische Szenarien: Conservative (vorsichtig), Realistic (wahrscheinlich) und Ambitious (mit vollem Einsatz).
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {/* Conservative */}
              <div className="rounded-xl border border-border bg-card/50 p-5">
                <div className="mb-4">
                  <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-700">
                    <span>🛡️</span>
                    <span>Conservative</span>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">Minimale Verbesserungen, geringes Risiko</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Monatsumsatz</div>
                    <div className="text-2xl font-bold text-foreground">{currency(scenarios.conservative.potential.revenue)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Zusätzlich</div>
                    <div className="text-lg font-semibold text-blue-600">+{currency(scenarios.conservative.additionalRevenuePerMonth)}</div>
                  </div>
                  <div className="pt-3 border-t border-border">
                    <div className="text-xs text-muted-foreground mb-1">Deals/Monat</div>
                    <div className="text-sm font-medium text-foreground">{num(scenarios.conservative.potential.deals, 1)}</div>
                  </div>
                </div>
              </div>

              {/* Realistic */}
              <div className="rounded-xl border-2 border-primary bg-primary/5 p-5 relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-bold">EMPFOHLEN</span>
                </div>
                <div className="mb-4 mt-2">
                  <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                    <span>🎯</span>
                    <span>Realistic</span>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">Erreichbare Ziele mit Fokus</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Monatsumsatz</div>
                    <div className="text-2xl font-bold text-primary">{currency(scenarios.realistic.potential.revenue)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Zusätzlich</div>
                    <div className="text-lg font-semibold text-primary">+{currency(scenarios.realistic.additionalRevenuePerMonth)}</div>
                  </div>
                  <div className="pt-3 border-t border-border">
                    <div className="text-xs text-muted-foreground mb-1">Deals/Monat</div>
                    <div className="text-sm font-medium text-foreground">{num(scenarios.realistic.potential.deals, 1)}</div>
                  </div>
                </div>
              </div>

              {/* Ambitious */}
              <div className="rounded-xl border border-border bg-card/50 p-5">
                <div className="mb-4">
                  <div className="inline-flex items-center gap-2 rounded-full bg-purple-100 px-3 py-1 text-sm font-semibold text-purple-700">
                    <span>🚀</span>
                    <span>Ambitious</span>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">Maximales Potenzial, voller Einsatz</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Monatsumsatz</div>
                    <div className="text-2xl font-bold text-foreground">{currency(scenarios.ambitious.potential.revenue)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Zusätzlich</div>
                    <div className="text-lg font-semibold text-purple-600">+{currency(scenarios.ambitious.additionalRevenuePerMonth)}</div>
                  </div>
                  <div className="pt-3 border-t border-border">
                    <div className="text-xs text-muted-foreground mb-1">Deals/Monat</div>
                    <div className="text-sm font-medium text-foreground">{num(scenarios.ambitious.potential.deals, 1)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Completeness Score */}
            <div className="mt-6 rounded-xl bg-muted/50 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-foreground mb-1">Datenvollständigkeit</div>
                  <p className="text-xs text-muted-foreground">Je vollständiger Ihre Daten, desto präziser die Szenarien</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-primary">{completenessScore}%</div>
                  <div className="text-xs text-muted-foreground">
                    {completenessScore >= 90 ? "Exzellent" : completenessScore >= 70 ? "Gut" : "Verbesserungsbedarf"}
                  </div>
                </div>
              </div>
              {completenessScore < 90 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    💡 Tipp: Füllen Sie fehlende Daten aus, um noch genauere Prognosen zu erhalten.
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* TOP 3 HEBEL */}
        <section id="section-top-hebel" className={`${card} mb-8 p-6`}>
          <div className="mb-5">
            <h2 className={sectionTitle}>Die drei Hebel mit dem größten Wirkungspotenzial</h2>
            <p className={sectionSub}>
              Abgeleitet aus Ihrer Ist-Situation: Die Maßnahmen, die rechnerisch den stärksten Einfluss auf Ihren Umsatz haben.
            </p>
          </div>

          {/* Activity Reality & Sales Cycle Warnings */}
          {computedMetrics?.activityCheck?.isActivityUnrealistic && (
            <div className="mb-5 rounded-xl border border-orange-300 bg-orange-50 p-4">
              <div className="flex items-center gap-2 text-orange-700 font-semibold text-sm mb-1">
                <AlertTriangle className="h-4 w-4" />
                Aktivitäts-Reality-Check
              </div>
              <div className="text-sm text-foreground">
                Um Ihr Ziel zu erreichen, müsste jeder Rep ca.{" "}
                <strong>{num(computedMetrics.activityCheck.requiredContactsPerRepFromNow, 0)} neue Kontakte/Monat</strong> generieren —{" "}
                aktuell sind es {num(computedMetrics.activityCheck.actualContactsPerRepPerMonth, 0)}.{" "}
                Das ist ein Faktor von {num(computedMetrics.activityCheck.requiredContactsPerRepFromNow / Math.max(1, computedMetrics.activityCheck.actualContactsPerRepPerMonth), 1)}x. Ohne Aktivitätssteigerung oder Conversion-Verbesserung ist das Ziel schwer erreichbar.
              </div>
            </div>
          )}

          {computedMetrics?.salesCycleWarning?.isSalesCycleTooLong && (
            <div className="mb-5 rounded-xl border border-red-300 bg-red-50 p-4">
              <div className="flex items-center gap-2 text-red-700 font-semibold text-sm mb-1">
                <AlertTriangle className="h-4 w-4" />
                Zeitfenster wird kritisch — Ihr Sales Cycle braucht Aufmerksamkeit
              </div>
              <div className="text-sm text-foreground">
                Ihr Sales Cycle beträgt ca. {num(computedMetrics.salesCycleWarning.salesCycleMonths, 0)} Monate. Es bleiben noch{" "}
                ca. {computedMetrics.salesCycleWarning.monthsRemainingApprox} Monate bis Jahresende.{" "}
                <strong>Deals, die Sie heute starten, schließen erst deutlich später.</strong>{" "}
                {computedMetrics.salesCycleWarning.dealsUnreachableDueToSalesCycle > 0 && (
                  <span>Ca. <strong className="text-red-700">{computedMetrics.salesCycleWarning.dealsUnreachableDueToSalesCycle} der noch benötigten Abschlüsse</strong> sind kurzfristig zeitlich nicht mehr erreichbar.</span>
                )}
              </div>
            </div>
          )}

          <div className="space-y-5">
            {topLevers.map((lever, index) => {
              const dimension = lever.dimension || inferDimensionFromTitle(lever.title);
              const metrics = getLeverMetrics(dimension, lever.title, {
                convContactToDiscovery: computedMetrics.convContactToDiscovery,
                benchmarkContactToDiscovery: computedMetrics.benchmarkContactToDiscovery,
                convDiscoveryToPresentation: computedMetrics.convDiscoveryToPresentation,
                benchmarkDiscoveryToPresentation: computedMetrics.benchmarkDiscoveryToPresentation,
                convPresentationToDeal: computedMetrics.convPresentationToDeal,
                benchmarkPresentationToDeal: computedMetrics.benchmarkPresentationToDeal,
              });
              const improvementPotential = metrics.potentialMetric - metrics.currentMetric;

              // Funnel position label
              const funnelPosition =
                dimension === "DEMAND" ? "Funnel-Eingang" :
                dimension === "CONVERSION" ? "Funnel-Mitte" :
                dimension === "SALES_QUALITY" ? "Discovery & Closing" :
                dimension === "PROCESS" ? "Pipeline & Follow-up" :
                dimension === "WEBSITE" ? "Online-Präsenz" :
                dimension === "REP_PERFORMANCE" ? "Team-Execution" :
                "Positionierung";

              return (
                <div key={index} className={softCard + " p-6"}>
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500/10 text-blue-600 font-bold text-lg">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <h3 className="text-lg font-bold text-foreground">
                            {lever.title}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                              {funnelPosition}
                            </span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              metrics.quickWinLevel === "Quick Win"
                                ? "bg-green-100 text-green-700"
                                : metrics.quickWinLevel === "Mittel"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-blue-100 text-blue-700"
                            }`}>
                              {metrics.quickWinLevel}
                            </span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-2xl font-bold text-blue-600">{currency(lever.monthlyImpact)}</div>
                          <div className="text-xs text-muted-foreground">/ Monat Potenzial</div>
                        </div>
                      </div>

                      {/* Warum */}
                      <div className="mb-4 p-3 rounded-lg bg-muted/50 border border-border/50">
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Warum dieser Hebel?</div>
                        <p className="text-sm text-foreground leading-relaxed">{lever.whyItMatters}</p>
                      </div>

                      {/* Conversion Gap */}
                      {improvementPotential > 0 && (
                        <div className="mb-4 p-3 rounded-lg bg-card border border-border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{metrics.metricLabel}</span>
                            <span className="text-xs font-semibold text-accent">+{num(improvementPotential, 1)}% möglich</span>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="text-muted-foreground">Aktuell: <strong>{num(metrics.currentMetric, 1)}%</strong></span>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            <span className="text-accent">Benchmark: <strong>{num(metrics.potentialMetric, 1)}%</strong></span>
                          </div>
                        </div>
                      )}

                      {/* Impact */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-lg bg-blue-50 px-3 py-2 text-center">
                          <div className="text-xs text-muted-foreground mb-1">Monatlich</div>
                          <div className="text-sm font-bold text-blue-700">{currency(lever.monthlyImpact)}</div>
                        </div>
                        <div className="rounded-lg bg-blue-50 px-3 py-2 text-center">
                          <div className="text-xs text-muted-foreground mb-1">Jährlich</div>
                          <div className="text-sm font-bold text-blue-700">{currency(lever.annualImpact)}</div>
                        </div>
                        <div className="rounded-lg bg-blue-50 px-3 py-2 text-center">
                          <div className="text-xs text-muted-foreground mb-1">% des Gaps</div>
                          <div className="text-sm font-bold text-blue-700">
                            {computedMetrics.monthlyRevenueGap > 0
                              ? pct((lever.monthlyImpact / computedMetrics.monthlyRevenueGap) * 100, 0)
                              : "–"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        {/* WEBSITE-HINWEIS: deaktiviert bis echte API verfügbar */}
        {/* DATENLÜCKEN */}
        {dataGaps && dataGaps.length > 0 && (
          <section className={`${card} mb-8 p-6`}>
            <div className="mb-5">
              <h2 className={sectionTitle}>Fehlende Datenpunkte — was noch unklar bleibt</h2>
              <p className={sectionSub}>
                Diese Kennzahlen fehlen aktuell oder sind nicht belastbar. Das begrenzt die Präzision der Analyse — und deutet oft auf blinde Flecken in der Steuerung hin.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {dataGaps.map((gap) => (
                <div
                  key={gap.id}
                  className={`rounded-2xl border p-5 ${
                    gap.severity === "critical"
                      ? "bg-red-50 border-red-300"
                      : gap.severity === "warning"
                      ? "bg-orange-50 border-orange-300"
                      : "bg-yellow-50 border-yellow-300"
                  }`}
                >
                  <div className="mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    <div className="text-sm font-medium text-foreground">{gap.field}</div>
                  </div>
                  <p className="text-sm text-foreground mb-3 font-medium">{gap.title}</p>
                  <div className="rounded-xl bg-white/50 p-3 mb-3">
                    <div className="text-xs uppercase tracking-[0.12em] text-foreground/80 mb-1">
                      Impact
                    </div>
                    <p className="text-sm text-foreground">{gap.impact}</p>
                  </div>
                  <div className="rounded-xl bg-white/50 p-3">
                    <div className="text-xs uppercase tracking-[0.12em] text-foreground/80 mb-1">
                      Empfehlung
                    </div>
                    <p className="text-sm text-foreground">{gap.recommendation}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CONVERSION BRIDGE — Übergang zur Handlungsempfehlung */}
        <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6">
          <div className="text-base font-semibold text-foreground mb-3">Was diese Analyse zusammengefasst zeigt</div>
          <div className="space-y-2 text-sm text-foreground/80 leading-relaxed mb-4">
            <p>✓ Die Ursachen hinter Ihrer Ziellücke sind identifiziert.</p>
            <p>✓ Die kritischen Stellen in Ihrem Funnel sind sichtbar.</p>
            <p>✓ Die drei Hebel mit dem größten Wirkungspotenzial sind priorisiert.</p>
          </div>
          <p className="text-sm font-medium text-foreground leading-relaxed">
            Die entscheidende Frage ist jetzt nicht mehr, <em>ob</em> Sie handeln müssen — sondern <em>wo</em> Sie beginnen sollten und in welcher Reihenfolge.
          </p>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Genau das beantwortet die personalisierte Handlungsempfehlung: Ein priorisierter 90-Tage-Plan mit konkreten Maßnahmen, klaren Verantwortlichkeiten und direktem Umsatzbezug.
          </p>
        </div>

        {/* CTA → Paywall */}
        <AdminBypassBanner navigate={navigate} />
        <section id="section-handlungsempfehlung" className="mt-8 rounded-3xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-accent/5 p-8 md:p-10 shadow-xl">
          <div className="grid gap-8 md:grid-cols-[1.3fr_0.7fr] md:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary uppercase tracking-wider mb-4">
                ⚡ Ihr nächster Schritt
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                Sie wissen, wo der Umsatz verloren geht. Jetzt kommt der Plan.
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Die Diagnose ist abgeschlossen. Die Handlungsempfehlung übersetzt sie in einen priorisierten Umsetzungsplan — mit konkreten Maßnahmen, einem 90-Tage-Fahrplan und klarem Fokus auf das, was den größten Hebel hat.
              </p>
              <div className="mt-5 flex flex-wrap gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> Ihre Top-3-Prioritäten</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> 90-Tage-Umsetzungsplan</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> Quick Wins für sofort</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> Direkter Umsatzbezug</span>
              </div>
            </div>
            <div className="flex flex-col items-center text-center gap-4">
              <div className="text-4xl font-bold text-foreground">149 €</div>
              <div className="text-sm text-muted-foreground">Einmalig · Sofort verfügbar</div>
              <button
                onClick={() => navigate(ROUTE_PATHS.PAYWALL)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-4 text-base font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg"
              >
                Zum personalisierten Plan
                <ArrowRight className="h-5 w-5" />
              </button>
              <p className="text-xs text-muted-foreground">Oder: Kostenloses Erstgespräch buchen</p>
            </div>
          </div>
        </section>
        </div>
        </div>
      </div>
    </div>
    </Layout>
  );
}

// Helper function for acquisition method labels
function getLabelForMethod(key: string): string {
  const labels: Record<string, string> = {
    inbound: "Inbound / Content",
    outbound: "Outbound / Cold",
    referral: "Empfehlungen",
    partner: "Partner",
    events: "Events / Messen",
    paid: "Paid Ads",
    social: "Social Selling",
    phone: "Telefon",
    email: "E-Mail",
    linkedin: "LinkedIn",
    xing: "Xing",
    referrals: "Empfehlungen",
    coldVisits: "Kaltbesuche",
    contentMarketing: "Content Marketing",
  };
  return labels[key] || key;
}

// ── Admin-Bypass-Banner ─────────────────────────────────────────────────────
function AdminBypassBanner({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  const { isAuthenticated } = useAdminStore();
  if (!isAuthenticated) return null;
  const handleBypass = () => {
    // Freischaltungs-Flag setzen (wird von ActionRecommendationsMasterAI geprüft)
    try { localStorage.setItem('salesforge_unlocked', 'true'); } catch { /* ignore */ }
    navigate(ROUTE_PATHS.ACTION_RECOMMENDATIONS);
  };
  return (
    <div className="mt-6 rounded-2xl border-2 border-dashed border-amber-400/60 bg-amber-50/60 px-6 py-4 flex items-center justify-between gap-4">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-0.5">
          🛡️ Admin-Zugang
        </div>
        <p className="text-sm text-amber-800">
          Sie sind als Admin eingeloggt — Sie können die Handlungsempfehlung ohne Zahlung öffnen.
        </p>
      </div>
      <button
        onClick={handleBypass}
        className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-600 transition-colors shadow-md"
      >
        Direkt ansehen
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

